import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff } from "lucide-react";
import {
  PLATFORMS, FAMILY_MEMBERS, ACCOUNT_STATUSES, GEO_TYPES, US_STATES, MAJOR_CITIES
} from "./constants";

export interface SocialAccount {
  id?: string;
  platform: string;
  geo_type: string;
  state_code: string | null;
  city_name: string | null;
  email_used: string | null;
  username: string | null;
  handle: string | null;
  password_stored: string | null;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  profile_url: string | null;
  follower_count: number | null;
}

const BLANK: SocialAccount = {
  platform: 'x',
  geo_type: 'state',
  state_code: null,
  city_name: null,
  email_used: null,
  username: null,
  handle: null,
  password_stored: null,
  status: 'pending_setup',
  assigned_to: null,
  notes: null,
  profile_url: null,
  follower_count: null,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account?: SocialAccount | null;
  onSave: (data: SocialAccount) => void;
  isPending?: boolean;
}

export function AccountDialog({ open, onOpenChange, account, onSave, isPending }: Props) {
  const [form, setForm] = useState<SocialAccount>(BLANK);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (account) {
      setForm(account);
    } else {
      setForm(BLANK);
    }
    setShowPassword(false);
  }, [account, open]);

  const set = (key: keyof SocialAccount, value: unknown) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (!form.platform) return;
    onSave(form);
  };

  const isEditing = !!account?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Account' : 'Add Account'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update social account details.' : 'Add a new social media account to the registry.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Platform */}
          <div className="space-y-1">
            <Label>Platform *</Label>
            <Select value={form.platform} onValueChange={v => set('platform', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: p.color === '#000000' ? '#374151' : p.color }}
                      />
                      {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={v => set('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_STATUSES.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Geo Type */}
          <div className="space-y-1">
            <Label>Geo Type</Label>
            <Select value={form.geo_type} onValueChange={v => set('geo_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {GEO_TYPES.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* State Code (if state or city) */}
          {(form.geo_type === 'state' || form.geo_type === 'city') && (
            <div className="space-y-1">
              <Label>State</Label>
              <Select
                value={form.state_code ?? ''}
                onValueChange={v => set('state_code', v || null)}
              >
                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>
                  {US_STATES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* City (if city) */}
          {form.geo_type === 'city' && (
            <div className="space-y-1 col-span-2">
              <Label>City</Label>
              <Select
                value={form.city_name ?? ''}
                onValueChange={v => set('city_name', v || null)}
              >
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>
                  {MAJOR_CITIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Username */}
          <div className="space-y-1">
            <Label>Username / Handle</Label>
            <Input
              value={form.username ?? ''}
              onChange={e => set('username', e.target.value || null)}
              placeholder="@ahp_texas"
            />
          </div>

          {/* Display Handle */}
          <div className="space-y-1">
            <Label>Display Name</Label>
            <Input
              value={form.handle ?? ''}
              onChange={e => set('handle', e.target.value || null)}
              placeholder="AHP Texas"
            />
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label>Email Used</Label>
            <Input
              type="email"
              value={form.email_used ?? ''}
              onChange={e => set('email_used', e.target.value || null)}
              placeholder="texas@americanhealthprices.com"
            />
          </div>

          {/* Assigned To */}
          <div className="space-y-1">
            <Label>Assigned To</Label>
            <Select
              value={form.assigned_to ?? ''}
              onValueChange={v => set('assigned_to', v || null)}
            >
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="">Unassigned</SelectItem>
                {FAMILY_MEMBERS.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Password */}
          <div className="space-y-1 col-span-2">
            <Label>Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password_stored ?? ''}
                onChange={e => set('password_stored', e.target.value || null)}
                placeholder="Account password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Profile URL */}
          <div className="space-y-1 col-span-2">
            <Label>Profile URL</Label>
            <Input
              value={form.profile_url ?? ''}
              onChange={e => set('profile_url', e.target.value || null)}
              placeholder="https://x.com/ahp_texas"
            />
          </div>

          {/* Follower Count */}
          <div className="space-y-1">
            <Label>Follower Count</Label>
            <Input
              type="number"
              value={form.follower_count ?? ''}
              onChange={e => set('follower_count', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="0"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1 col-span-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes ?? ''}
              onChange={e => set('notes', e.target.value || null)}
              placeholder="Any notes about this account..."
              rows={2}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending || !form.platform}>
            {isPending ? 'Saving...' : isEditing ? 'Update Account' : 'Add Account'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
